precision highp float;

uniform float time;
uniform float progress;
uniform float brightness;
varying float vAlpha;
varying float vRandom;

float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}

float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);

  float a = hash(i);
  float b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0));
  float d = hash(i + vec2(1.0, 1.0));

  vec2 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);

  return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
}

float fbm(vec2 p) {
  float v   = 0.0;
  float amp = 0.5;
  for (int i = 0; i < 3; i++) {
    v   += amp * noise(p);
    p   *= 2.07;
    amp *= 0.48;
  }
  return v;
}

float shieldSDF(vec2 p) {
  p.x = abs(p.x);

  float topArc = p.y - 0.35 + (p.x * p.x * 0.4);
  float side   = p.x + (p.y * 0.2) - 0.32;
  if (p.y < 0.0) {
    float ay = abs(p.y);
    side += ay * sqrt(ay) * 0.8;
  }

  return max(topArc, side);
}

void main() {
  vec2  uv = vec2(gl_PointCoord.x - 0.5, 0.5 - gl_PointCoord.y);
  vec2  p  = uv * 1.2;
  float d  = shieldSDF(p);

  float shieldFill = smoothstep(0.025, -0.025, d);
  if (shieldFill < 0.05) discard;

  float halo = smoothstep(0.10, 0.0, d) * (1.0 - shieldFill);
  halo *= 0.30;

  float edgeGlow = exp(-abs(d) * 26.0) * 0.85;

  vec2  ncoord    = uv * 4.5;
  float timeDrift = time * 0.10 + vRandom * 10.0;
  float n1 = fbm(ncoord + vec2( timeDrift,         time * 0.05));
  float n2 = fbm(ncoord * 1.3 + vec2(-timeDrift * 0.45, time * 0.08));
  float n  = mix(n1, n2, 0.38);

  vec3 colNight  = vec3(0.12, 0.00, 0.00);
  vec3 colDeep   = vec3(0.40, 0.01, 0.01); 
  vec3 colPure   = vec3(0.88, 0.03, 0.02);  
  vec3 colHot    = vec3(1.00, 0.20, 0.04);  
  vec3 colIce    = vec3(1.00, 0.42, 0.10);  

  vec3 baseCol = mix(
    mix(colNight, colDeep, clamp(vRandom * 3.0, 0.0, 1.0)),
    mix(
      mix(colDeep, colPure, clamp((vRandom - 0.33) * 3.0, 0.0, 1.0)),
      mix(colPure, colHot,  clamp((vRandom - 0.66) * 3.0, 0.0, 1.0)),
      step(0.66, vRandom)
    ),
    step(0.33, vRandom)
  );

  float heat   = clamp(vAlpha - 1.0, 0.0, 1.0);
  float heatSq = heat * heat;
  baseCol = mix(baseCol, colHot, heatSq * 0.70);
  baseCol += vec3(heatSq * 0.45, heatSq * 0.02, 0.0);

  float distCenter = length(uv);
  float innerGlow  = 1.0 - smoothstep(0.0, 0.38, distCenter);
  baseCol += innerGlow * mix(colDeep, colIce, innerGlow) * 0.22;

  baseCol = mix(baseCol, colIce, edgeGlow * 0.60);

  baseCol += colDeep * halo * 0.65;

  baseCol *= (0.80 + 0.25 * n);

  float pr = clamp(progress, 0.0, 1.0);
  float b  = max(brightness, 0.0);
  baseCol  = mix(vec3(0.0), baseCol, pr);
  baseCol *= (1.0 + b * 0.35);

  baseCol = pow(clamp(baseCol, 0.0, 1.0), vec3(0.92));

  float normalAlpha = clamp(vAlpha, 0.0, 1.0);
  float alpha       = shieldFill * normalAlpha
                    + halo * normalAlpha
                    + edgeGlow * shieldFill * normalAlpha * 0.5;
  float finalAlpha  = clamp(alpha * pr * normalAlpha, 0.0, 1.0);

  gl_FragColor = vec4(baseCol * normalAlpha, finalAlpha);
}
